import { Card } from "@attmos/flowstack-ui";
import { Boxes, FileBox } from "lucide-react";
import { Link } from "react-router-dom";

import type { Artifact } from "@/api/artifacts";
import { ArtifactDownloadButton } from "@/components/artifacts/ArtifactDownloadButton";
import { shortHash } from "@/lib/hash-display";
import { cn } from "@/lib/utils";

/**
 * One-card view of a typed-object artifact. Used in the Run-detail
 * Outputs tab and on the project-scoped artifact list.
 *
 * Click-through navigates to the artifact detail page, which VIZ U3.1
 * finally lands. Until then this card linked at `/artifacts?focus=…`,
 * a query parameter the list page never read — so the click went to an
 * unfiltered catalog table and looked like nothing had happened.
 */
export interface RunArtifactCardProps {
  artifact: Artifact;
  className?: string;
}

export function RunArtifactCard({ artifact, className }: RunArtifactCardProps): JSX.Element {
  return (
    <Card
      className={cn(
        "group relative flex items-start transition-colors hover:border-border-strong hover:bg-surface-2",
        className
      )}
    >
      <Link
        to={`/artifacts/${encodeURIComponent(artifact.artifact_id)}`}
        className="flex min-w-0 flex-1 items-start gap-3 p-4"
      >
        <div className="rounded-md bg-surface-2 p-2 text-muted-foreground group-hover:text-foreground">
          {artifact.typed_object === "ABFEEstimate" ? (
            <Boxes className="h-5 w-5" aria-hidden />
          ) : (
            <FileBox className="h-5 w-5" aria-hidden />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-sm font-medium text-foreground">
              {artifact.typed_object}
            </h3>
            <span className="rounded-sm bg-surface-2 px-1 font-mono text-[10px] text-muted-foreground">
              v{artifact.schema_version}
            </span>
          </div>
          <p
            className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground"
            title={artifact.content_hash}
          >
            {shortHash(artifact.content_hash)}
          </p>
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            {artifact.producer_tile && (
              <span>
                from <span className="font-mono">{artifact.producer_tile}</span>
              </span>
            )}
            <time dateTime={artifact.created_at}>
              {new Date(artifact.created_at).toLocaleString()}
            </time>
          </div>
        </div>
      </Link>
      {/* Sibling of the Link, not a child: a <button> inside an <a> is invalid
          HTML and traps the click for both. #619 — this is the control that
          finally lets a user retrieve the result their run produced. */}
      <div className="shrink-0 self-center pr-2">
        <ArtifactDownloadButton contentHash={artifact.content_hash} compact />
      </div>
    </Card>
  );
}
